import React, { useMemo, useState } from "react";
import clsx from "clsx";
import { ChevronDown } from "lucide-react";
import Card from "./Card";
import type { CardProps } from "./Card";

export type CollapsibleCardProps = {
  // Title area
  title?: React.ReactNode;
  headerIcon?: React.ReactNode; // optional icon before title; defaults to ChevronDown used as toggle
  headerActions?: React.ReactNode; // right side actions
  headerClassName?: string;

  // Footer area
  footer?: React.ReactNode; // footer content (e.g., link area)
  footerClassName?: string;

  // Collapse behavior
  defaultCollapsed?: boolean; // uncontrolled initial state
  collapsed?: boolean; // controlled state
  onToggle?: (collapsed: boolean) => void;
  showChevron?: boolean; // default true

  // Body content
  children?: React.ReactNode;
  bodyClassName?: string;

  // Card styling (passes through to Card)
  cardProps?: Partial<CardProps>;
  className?: string; // extra classes on root Card
};

/**
 * Reusable Card with header, body, footer and collapsible behavior.
 * Styles follow the DealDetailPanel visual: rounded-xl, border-gray-200, bg-white, subtle shadow.
 */
export default function CollapsibleCard({
  title,
  headerIcon,
  headerActions,
  headerClassName,
  footer,
  footerClassName,
  defaultCollapsed = false,
  collapsed: collapsedProp,
  onToggle,
  showChevron = true,
  children,
  bodyClassName,
  cardProps,
  className = "",
}: CollapsibleCardProps) {
  const isControlled = typeof collapsedProp === "boolean";
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  const collapsed = isControlled ? (collapsedProp as boolean) : internalCollapsed;

  const toggle = () => {
    const next = !collapsed;
    if (!isControlled) setInternalCollapsed(next);
    onToggle?.(next);
  };

  // Compose header icon: default chevron
  const LeftIcon = useMemo(() => {
    if (headerIcon) return <span className="shrink-0">{headerIcon}</span>;
    return (
      <ChevronDown
        className={clsx(
          "w-4 h-4 transition-transform shrink-0",
          collapsed ? "-rotate-90" : "rotate-0"
        )}
      />
    );
  }, [headerIcon, collapsed]);

  return (
    <Card
      bgClass={cardProps?.bgClass ?? "bg-white"}
      borderClass={cardProps?.borderClass ?? "border border-gray-200"}
      roundedClass={cardProps?.roundedClass ?? "rounded-xl"}
      shadow={cardProps?.shadow ?? "custom"}
      shadowClass={cardProps?.shadowClass ?? "shadow-[0_1px_1px_0_rgba(0,0,0,0.05)]"}
      className={clsx("", className)}
    >
      {/* Header */}
      <div className={clsx("flex items-end justify-between px-3 py-2", headerClassName)}>
        <button
          type="button"
          aria-expanded={!collapsed}
          onClick={toggle}
          className={clsx(
            "flex items-end gap-1 text-sm leading-5 font-medium text-gray-800 hover:text-gray-700",
            showChevron ? "" : "pointer-events-none"
          )}
        >
          {showChevron && LeftIcon}
          {title && <span className="truncate">{title}</span>}
        </button>
        {headerActions && <div className="flex items-center gap-1">{headerActions}</div>}
      </div>

      {/* Body */}
      {!collapsed && <div className={clsx("px-3", bodyClassName)}>{children}</div>}

      {/* Footer */}
      {!collapsed && footer && <div className={clsx("px-3 pb-3", footerClassName)}>{footer}</div>}
    </Card>
  );
}
